import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { SyndicationSettingsRepository } from '~/repositories/settings/syndicationSettingsRepository';
import type { SyndicationSettings, SyndicationSettingsCompanyPortalTypedSettingsWithOverridesDocument } from '~/swagger/settings/Api';
export const [SyndicationSettingsProvider, useSyndicationSettings] = createMagicDoorContext('SyndicationSettings', () => {
  const repo = new SyndicationSettingsRepository();

  const [isLoading, setIsLoading] = createSignal(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [allSyndicationSettings, setAllSyndicationSettings] = createSignal<
    SyndicationSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
  >();

  const getAllSyndicationSettings = async () => {
    try {
      setIsLoading(true);
      const json = await repo.getAllSyndicationSettings();
      setAllSyndicationSettings(json);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateSyndicationSettings = async (payload: SyndicationSettings) => {
    try {
      setIsLoading(true);
      await repo.updateSyndicationSettings(payload);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateOverrideSyndicationSettings = async (level: string, levelId: string, payload: SyndicationSettings) => {
    try {
      setIsLoading(true);
      const res = await repo.updateSyndicationSettingsByLevel(level, levelId, payload);
      return res;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const addOverrideSettings = async (level: string, levelId: string, payload: SyndicationSettings) => {
    try {
      setIsLoading(true);
      await repo.updateSyndicationSettingsByLevel(level, levelId, payload);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const removeOverrideSettings = async (level: string, levelId: string) => {
    try {
      setIsLoading(true);
      await repo.deleteSyndicationSettingsByLevel(level, levelId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const getSyndicationSettingsByLevel = async (level: string, levelId: string) => {
    try {
      setIsLoading(true);
      const json = await repo.getSyndicationSettingsByLevel(level, levelId);
      return json;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    error,
    isLoading,
    allSyndicationSettings,
    getAllSyndicationSettings,
    updateSyndicationSettings,
    addOverrideSettings,
    updateOverrideSyndicationSettings,
    removeOverrideSettings,
    getSyndicationSettingsByLevel,
  };
});
